import React, { useEffect, useMemo, useState } from "react";
import { observer } from "mobx-react-lite";
import { FTPAutoComplete, FTPRadioGroup, FTPInput } from "../../components-new";
import { FormProvider, useForm } from "react-hook-form";
import { useStore } from "../../helpers";
import { GENDERS, Service_request_level } from "../../features/beneficiary-management-v2";
import { yupResolver } from "@hookform/resolvers/yup";
import { safe_get } from "../../report-visuals";
import { serviceRequestValidations } from "./service-request-validation-schema";
import { subjectDefaultValues } from "./service-request-types";
import { Fade } from "react-awesome-reveal";
import { useAddServiceRequestSubject, useUpdateServiceRequestSubject } from "./sr-hooks";
import { FamilyMembers } from "./family-members";
import { AddSubjectFromVoterList } from "./AddSubjectFromVoterList";
import { RNBoothSelector } from "../../components/form-components";
import { toJS } from "mobx";
import { useLocation, useParams } from "react-router";
import { useNavigate } from "react-router-dom";
import { FormButtons } from "../../components";
import { FetchFromFacialRecognitionButton } from "../../components-new/face-recognition-button";
import { useTranslation } from "react-i18next";
import VoiceRecorder from "../../components-new/voice-record-recognition";
import { clone } from "lodash";
import ServiceRequestCustomFields from "../../project-properties/components/ServiceRequestCustomFields";
import VoterPhotoCapture from "./sr-components/voter-photo-capture";
import { resolveUploadedPhoto } from "./sr-components/apply-voter-photo-result";

const IS_OUTSIDE_VOTER = [
    { label: "No", value: false },
    { label: "Yes", value: true },
];

/**
 * Default Service Request Form Configuration
 *
 * This represents the current/existing form.
 *
 * If a project does not have SERVICE_REQUEST_FORM
 * configured in Project Properties, this configuration
 * will automatically be used.
 */
const DEFAULT_SERVICE_REQUEST_FORM_CONFIG = {
    fields: {
        name: {
            visible: true,
            required: true,
        },

        voterId: {
            visible: true,
            required: true,
        },

        serviceRequestLevel: {
            visible: true,
            required: true,
            reference: false,
        },

        // Match ServiceRequestFormConfig defaults (hidden unless enabled in properties)
        aadhaar: {
            visible: false,
            required: false,
        },

        phone: {
            visible: false,
            required: false,
        },

        whatsapp: {
            visible: false,
            required: false,
        },

        relativeName: {
            visible: true,
            required: false,
        },

        caste: {
            visible: false,
            required: false,
        },

        address: {
            visible: true,
            required: false,
        },

        dob: {
            visible: true,
            required: false,
        },

        age: {
            visible: true,
            required: false,
        },

        gender: {
            visible: true,
            required: false,
        },

        booth: {
            visible: true,
            required: false,
        },

        voterPresence: {
            visible: true,
            required: false,
        },

        otherAcVoter: {
            visible: true,
            required: false,
        },

        familyMembers: {
            visible: true,
            required: false,
        },

        camera: {
            visible: true,
            required: false,
        },
    },

    features: {
        addFromVoterList: true,
        facialRecognition: true,
        voiceSearch: true,
    },
};

type VoterPhotoFile = {
    location: string;
    name: string;
    path: string;
    type: string;
};

const AddServiceRequestSubject = observer((props: any) => {
    const { projectStore, voterListStore, serviceRequestsStore } = useStore();

    const { activeServiceRequest, selectedFamilyMembers, setFamilyMembers } = serviceRequestsStore;

    const subjectID = safe_get(activeServiceRequest, "subject.id", null);
    const { activeVoter } = voterListStore;

    const { mutate, isLoading, isSuccess } = useAddServiceRequestSubject();
    const [photo, setPhoto] = useState<VoterPhotoFile | null>(null);
    const [photoS3Path, setPhotoS3Path] = useState<string | null>(null);
    const { t } = useTranslation("addServiceRequestSubject");

    const { projectid } = useParams();

    /**
     * ---------------------------------------------------------
     * PROJECT SERVICE REQUEST CONFIGURATION
     * ---------------------------------------------------------
     *
     * Reads SERVICE_REQUEST_FORM from Project Properties.
     *
     * If the property doesn't exist, current/default form
     * configuration is used.
     */
    // Touch observable so this observer re-renders after property sync
    const activeProject = projectStore.activeProject;

    const projectServiceRequestConfig = projectStore.getProjectProperty("service_request_form", null);

    const serviceRequestConfig = useMemo(() => {
        const projectFields = projectServiceRequestConfig?.fields || {};
        const mergedFields = {
            ...DEFAULT_SERVICE_REQUEST_FORM_CONFIG.fields,
        };

        Object.keys(projectFields).forEach((key) => {
            mergedFields[key] = {
                ...(mergedFields[key] || {}),
                ...projectFields[key],
            };
        });

        return {
            ...DEFAULT_SERVICE_REQUEST_FORM_CONFIG,

            fields: mergedFields,

            features: {
                ...DEFAULT_SERVICE_REQUEST_FORM_CONFIG.features,
                ...(projectServiceRequestConfig?.features || {}),
            },

            customFields: Array.isArray(projectServiceRequestConfig?.customFields)
                ? projectServiceRequestConfig.customFields
                : [],
        };
    }, [projectServiceRequestConfig, activeProject]);

    const isRequestFieldVisible = (fieldName: string) =>
        serviceRequestConfig?.fields?.[fieldName]?.visible !== false;

    const requiredLabel = (fieldName: string, label: string) =>
        serviceRequestConfig?.fields?.[fieldName]?.required === true ? `${label} ` : label;

    const profilePhoto =
        photoS3Path ||
        safe_get(activeVoter, "profile_photo", "") ||
        safe_get(activeVoter, "profile.profile_photo", "") ||
        "";

    const profileOptions = projectStore.getProjectProperty("profile_questions", {});

    const projectProperties = projectStore.getProjectProperty("project_state", {});

    const faceScan = safe_get(projectProperties, "faceScan", null);

    const formatDateForInput = (date?: string) => {
        if (!date) return "";

        if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
            return date;
        }

        if (/^\d{2}\/\d{2}\/\d{4}$/.test(date)) {
            const [d, m, y] = date.split("/");
            return `${y}-${m}-${d}`;
        }

        const parsed = new Date(date);

        return isNaN(parsed.getTime()) ? "" : parsed.toISOString().split("T")[0];
    };

    const GENDERS_TRANSLATED = [
        {
            label: t("male"),
            value: "Male",
        },
        {
            label: t("female"),
            value: "Female",
        },
        {
            label: t("others"),
            value: "Others",
        },
    ];

    const SERVICE_REQUEST_LEVEL_TRANSLATED = [
        {
            label: t("individual"),
            value: "Individual",
        },
        {
            label: t("family"),
            value: "Family",
        },
        {
            label: t("community"),
            value: "Community",
        },
    ];
    //
    // const SERVICE_REQUEST_LEVEL_OPTIONS = serviceRequestConfig?.fields?.serviceRequestLevel?.reference
    //     ? [
    //           { label: "Self", value: "Self" },
    //           { label: "Reference", value: "Reference" },
    //           { label: "Community", value: "Community" },
    //       ]
    //     : SERVICE_REQUEST_LEVEL_TRANSLATED;
    const SERVICE_REQUEST_LEVEL_OPTIONS = serviceRequestConfig?.fields?.serviceRequestLevel?.reference
        ? [
              { label: "Self", value: "Self" },
              { label: "Reference", value: "Reference" },
              { label: "Community", value: "Community" },
          ]
        : SERVICE_REQUEST_LEVEL_TRANSLATED;

    const defaultRequestLevel = serviceRequestConfig?.fields?.serviceRequestLevel?.reference
        ? "Self"
        : "Individual";

    const IS_OUTSIDE_VOTER_TRANSLATED = [
        {
            label: t("no"),
            value: false,
        },
        {
            label: t("yes"),
            value: true,
        },
    ];

    const casteOptions = safe_get(profileOptions, "caste", []);

    const navigate = useNavigate();

    const { navigateNext } = props;

    const location = useLocation();

    const isEditMode = location.pathname.includes("editrequest");

    const normalizeIndianPhone = (value?: string) => {
        if (!value) return "";

        const digits = value.replace(/\D/g, "");

        return digits.length >= 10 ? digits.slice(-10) : digits;
    };

    const subjectCustomFields = serviceRequestConfig.customFields.filter(
        (field) => field.section === "subject" && field.visible !== false
    );

    const methods = useForm({
        defaultValues: {
            ...subjectDefaultValues,
            // @ts-ignore
            profile: {
                ...subjectDefaultValues.profile,
                request_level: defaultRequestLevel,
            },
        },

        // @ts-ignore
        resolver: yupResolver(serviceRequestValidations),

        context: {
            projectid,
            serviceRequestConfig,
            familyMemberCount: Array.isArray(toJS(selectedFamilyMembers))
                ? toJS(selectedFamilyMembers).length
                : 0,
            profilePhoto,
        },
    });

    // Keep default Self / Individual selected for the active option set
    useEffect(() => {
        if (isEditMode && subjectID) return;

        const currentLevel = methods.getValues("profile.request_level");
        const validValues = SERVICE_REQUEST_LEVEL_OPTIONS.map((option) => option.value);

        if (!currentLevel || !validValues.includes(currentLevel)) {
            methods.setValue("profile.request_level", defaultRequestLevel);
        }
    }, [defaultRequestLevel, serviceRequestConfig]);

    // @ts-ignore
    const dob = methods.watch("dob");

    useEffect(() => {
        if (dob) {
            // @ts-ignore
            const birthDate = new Date(dob);

            const today = new Date();

            let age = today.getFullYear() - birthDate.getFullYear();

            const monthDiff = today.getMonth() - birthDate.getMonth();

            const dayDiff = today.getDate() - birthDate.getDate();

            if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
                age--;
            }

            // @ts-ignore
            methods.setValue("profile.age", age);
        }
    }, [dob]);

    useEffect(() => {
        const voterID = safe_get(activeVoter, "id", null);

        const { profile, ...others } = activeVoter;

        if (voterID) {
            setPhotoS3Path(
                safe_get(activeVoter, "profile_photo", null) ||
                    safe_get(activeVoter, "profile.profile_photo", null) ||
                    null
            );

            methods.reset({
                additional: {
                    booth: safe_get(others, "part", null),
                },

                profile: {
                    ...profile,
                    gender: profile?.gender,
                    request_level: profile?.request_level || defaultRequestLevel,
                },

                dob: formatDateForInput(profile?.dob),

                caste: casteOptions.find((opt) => opt.value === activeVoter.caste),

                ...others,
            });
        } else if (subjectID && isEditMode) {
            const subjectData = activeServiceRequest.subject?.subject || activeServiceRequest.subject;

            setPhotoS3Path(
                safe_get(subjectData, "profile_photo", null) ||
                    safe_get(activeServiceRequest, "subject.profile_photo", null) ||
                    null
            );

            const whatsapp = subjectData.whatsapp;

            const phone = subjectData.phone;

            if (subjectData?.phone) {
                methods.setValue("phone", normalizeIndianPhone(subjectData.phone));
            }

            if (subjectData?.whatsapp) {
                methods.setValue("whatsapp", normalizeIndianPhone(subjectData.whatsapp));
            }

            const sameAsPhonePrefill = whatsapp && phone && whatsapp === phone;

            setFamilyMembers(
                toJS(
                    activeServiceRequest.subject?.subject?.additional?.family_members_list
                        ? activeServiceRequest.subject?.subject?.additional?.family_members_list
                        : activeServiceRequest.subject?.additional?.family_members_list
                )
            );

            methods.reset({
                ...activeServiceRequest.subject?.subject,

                dob: formatDateForInput(subjectData?.profile?.dob),

                sameAsPhone: sameAsPhonePrefill,

                additional: {
                    booth: toJS(activeServiceRequest?.subject?.additional?.booth),
                },
            });

            const phoneVal = subjectData?.phone;

            const whatsappVal = subjectData?.whatsapp;

            methods.setValue("phoneNotAvailable", !phoneVal);

            methods.setValue("sameAsPhone", phoneVal && phoneVal === whatsappVal);
        } else {
            setPhotoS3Path(null);
            setPhoto(null);

            methods.reset({
                ...subjectDefaultValues,
                // @ts-ignore
                profile: {
                    ...subjectDefaultValues.profile,
                    request_level: defaultRequestLevel,
                },
                additional: {
                    booth: {},
                    village: {},
                    family_members_list: [],
                },
                phone: "",
                whatsapp: "",
                aadharid: "",
                caste: "",
                is_outsider: false,
                sameAsPhone: false,
                phoneNotAvailable: false,
            });

            setFamilyMembers([]);
        }
    }, [activeVoter, subjectID, defaultRequestLevel, isEditMode]);

    // @ts-ignore
    const isOtherACVoter = methods.watch("is_outsider");

    const phone = methods.watch("phone");

    const phoneNotAvailable = methods.watch("phoneNotAvailable");

    const sameAsPhone = methods.watch("sameAsPhone");

    useEffect(() => {
        if (phoneNotAvailable) {
            methods.setValue("phone", "");

            methods.setValue("whatsapp", "");

            methods.setValue("sameAsPhone", false);

            return;
        }

        const normalizedPhone = normalizeIndianPhone(phone);

        if (sameAsPhone && normalizedPhone.length === 10) {
            methods.setValue("whatsapp", normalizedPhone);
        }

        if (!sameAsPhone) {
            methods.setValue("whatsapp", "");
        }
    }, [phone, phoneNotAvailable, sameAsPhone]);

    const clone_voter_filters = clone(voterListStore.voter_filters);

    const {
        mutate: updateSubject,
        isLoading: isUpdating,
        isSuccess: isUpdated,
    } = useUpdateServiceRequestSubject();

    const voterPhotoVoterId =
        methods.watch("profile.voterid") ||
        safe_get(activeVoter, "profile.voterid", null) ||
        safe_get(activeVoter, "voterid", null) ||
        "";

    const validateSubjectCustomFields = () => {
        let isValid = true;

        subjectCustomFields.forEach((field) => {
            if (!field.required) return;

            const value = methods.getValues(`customFields.${field.id}` as any);

            const isEmpty =
                field.type === "checkbox"
                    ? value !== true
                    : value === undefined || value === null || String(value).trim() === "";

            if (isEmpty) {
                methods.setError(`customFields.${field.id}` as any, {
                    type: "required",
                    message: `${field.label} is required`,
                });

                isValid = false;
            }
        });

        return isValid;
    };

    const onSubmit = (data) => {
        if (!validateSubjectCustomFields()) {
            return;
        }

        let finalData = {
            ...data,
        };

        delete finalData.sameAsPhone;

        /**
         * If Family Members is disabled
         * for this project, don't send
         * previously selected family members.
         */
        finalData.additional = finalData.additional || {};

        finalData.additional.family_members_list = serviceRequestConfig.fields.familyMembers.visible
            ? selectedFamilyMembers
                ? [...toJS(selectedFamilyMembers)]
                : []
            : [];

        finalData.profile_photo =
            photoS3Path ||
            finalData.profile_photo ||
            safe_get(activeVoter, "profile_photo", "") ||
            safe_get(activeVoter, "profile.profile_photo", "");

        console.log("FINAL PROFILE PHOTO:", finalData.profile_photo);
        console.log("FINAL SUBJECT DATA:", finalData);
        if (!subjectID) {
            mutate({
                ...activeVoter,
                ...finalData,
            });
        } else {
            updateSubject({
                ...activeServiceRequest.subject,

                subject: {
                    ...finalData,
                },
            });
        }

        setTimeout(() => {
            navigateNext();
        }, 1000);
    };

    const onError = (errors) => {
        console.log("SERVICE REQUEST SUBJECT ERRORS", errors);
    };

    const handleCancel = () => {
        methods.reset({
            ...subjectDefaultValues,
            // @ts-ignore
            profile: {
                ...subjectDefaultValues.profile,
                request_level: defaultRequestLevel,
            },
        });

        setPhoto(null);
        setPhotoS3Path(null);
        setFamilyMembers([]);
        voterListStore.update_active_voter({});

        if (!isEditMode) {
            serviceRequestsStore.setActiveServiceRequest(null);
            serviceRequestsStore.setSubjectId(-1);
            localStorage.removeItem("sr-subject");
            localStorage.removeItem("sr-subject-saved");
        }

        navigate(-1);
    };

    if (isLoading) {
        return null;
    }

    return (
        <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(onSubmit, onError)} className="flex flex-col gap-4">
                {/* =====================================================
                    TOP ACTIONS
                ====================================================== */}

                <div className="flex flex-wrap items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                        {/* Add From Voter List */}
                        {serviceRequestConfig.features.addFromVoterList && <AddSubjectFromVoterList />}

                        {/* Facial Recognition */}
                        {faceScan && serviceRequestConfig.features.facialRecognition && (
                            <FetchFromFacialRecognitionButton />
                        )}
                    </div>

                    {/* Camera + Voice Search */}
                    <div className="flex items-center gap-2">
                        {/* Capture Voter Photo */}
                        {isRequestFieldVisible("camera") && (
                            <div className="flex flex-col items-end">
                                {serviceRequestConfig.fields.camera?.required && (
                                    <p className="mb-1 text-xs font-semibold text-slate-700">Photo *</p>
                                )}
                                <VoterPhotoCapture
                                    voterId={voterPhotoVoterId}
                                    currentPhotoS3Path={photoS3Path}
                                    onUploaded={(result) => {
                                        const { photoUrl, voter } = resolveUploadedPhoto(result);
                                        console.log("PHOTO URL FROM CAPTURE:", photoUrl);
                                        setPhotoS3Path(photoUrl);
                                        if (voter) voterListStore.update_active_voter(voter);
                                    }}
                                />
                                {(methods.formState.errors as any)?.profile_photo?.message && (
                                    <p className="mt-1 text-xs text-red-500">
                                        {(methods.formState.errors as any).profile_photo.message}
                                    </p>
                                )}
                            </div>
                        )}

                        {serviceRequestConfig.features.voiceSearch && (
                            <div className="flex h-11 w-11 items-center justify-center rounded-full border border-gray-300 bg-white shadow-sm hover:bg-gray-50">
                                <VoiceRecorder
                                    buttonLabel=""
                                    onResult={async (text) => {
                                        const updatedFilters = {
                                            ...clone_voter_filters,
                                            name: "",
                                            voterid: "",
                                            offset: 0,
                                        };

                                        const voterIdPattern = /^[A-Za-z]{3}\d{7}$/;

                                        if (voterIdPattern.test(text)) {
                                            updatedFilters.voterid = text;
                                        } else {
                                            updatedFilters.name = text;
                                        }

                                        await voterListStore.update_filters(updatedFilters);

                                        voterListStore.setShowVoterlist(true);
                                    }}
                                />
                            </div>
                        )}
                    </div>
                </div>

                {/* =====================================================
                    MAIN FORM
                ====================================================== */}

                <div className="flex w-full flex-col justify-between gap-8 sm:flex-row">
                    {/* =================================================
                        BENEFICIARY DETAILS
                    ================================================== */}

                    <div
                        className={`flex w-full flex-col gap-y-4 ${
                            serviceRequestConfig.fields.familyMembers.visible ? "sm:w-[60%]" : "w-full"
                        }`}
                    >
                        <div className="grid grid-cols-1 items-start gap-4 sm:grid-cols-2 xl:grid-cols-3">
                            {/* ===========================
                                NAME
                            ============================ */}

                            {serviceRequestConfig.fields.name.visible && (
                                <FTPInput
                                    className="col-span-1 min-w-0"
                                    name="profile.name_eng"
                                    label={requiredLabel("name", t("name"))}
                                />
                            )}

                            {/* ===========================
                                VOTER ID
                            ============================ */}

                            {serviceRequestConfig.fields.voterId.visible && (
                                <FTPInput
                                    className="col-span-1 min-w-0"
                                    name="profile.voterid"
                                    label={requiredLabel("voterId", "Voter ID")}
                                />
                            )}

                            {/* ===========================
                                SERVICE REQUEST LEVEL
                            ============================ */}

                            {serviceRequestConfig.fields.serviceRequestLevel.visible && (
                                <div className="col-span-1 min-w-0">
                                    <FTPRadioGroup
                                        name="profile.request_level"
                                        label={requiredLabel(
                                            "serviceRequestLevel",
                                            t("service_request_level")
                                        )}
                                        options={SERVICE_REQUEST_LEVEL_OPTIONS}
                                    />
                                </div>
                            )}

                            {/* ===========================
                                AADHAAR
                            ============================ */}

                            {serviceRequestConfig.fields.aadhaar.visible && (
                                <FTPInput
                                    className="col-span-1 min-w-0"
                                    name="aadharid"
                                    label={requiredLabel("aadhaar", "Aadhar Number")}
                                />
                            )}

                            {/* ===========================
                                PHONE
                            ============================ */}

                            {serviceRequestConfig.fields.phone.visible && (
                                <>
                                    {!phoneNotAvailable && (
                                        <FTPInput
                                            className="col-span-1 min-w-0"
                                            name="phone"
                                            label={requiredLabel("phone", "Phone Number")}
                                            numericOnly
                                            maxLength={10}
                                        />
                                    )}

                                    {!phone && (
                                        <div className="col-span-1 flex min-h-[72px] min-w-0 items-end pb-2">
                                            <div className="flex items-center gap-2">
                                                <input
                                                    type="checkbox"
                                                    {...methods.register("phoneNotAvailable")}
                                                    className="h-4 w-4 cursor-pointer accent-blue-600"
                                                />

                                                <label className="cursor-pointer text-sm text-gray-600">
                                                    Phone number not available
                                                </label>
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}

                            {/* ===========================
                                WHATSAPP
                            ============================ */}

                            {serviceRequestConfig.fields.whatsapp.visible &&
                                !phoneNotAvailable &&
                                normalizeIndianPhone(phone)?.length === 10 && (
                                    <div className="col-span-1 flex min-w-0 flex-col gap-2 sm:col-span-2 xl:col-span-2">
                                        <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
                                            <div className="min-w-0 flex-1">
                                                <FTPInput
                                                    name="whatsapp"
                                                    label={requiredLabel("whatsapp", "Whatsapp Number")}
                                                    numericOnly
                                                    maxLength={10}
                                                    disabled={sameAsPhone}
                                                />
                                            </div>

                                            <div className="flex shrink-0 items-center pb-2">
                                                <input
                                                    type="checkbox"
                                                    {...methods.register("sameAsPhone")}
                                                    className="h-4 w-4 cursor-pointer accent-blue-600"
                                                />

                                                <label className="ml-2 cursor-pointer whitespace-nowrap text-sm text-gray-600">
                                                    Same as phone
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                )}

                            {/* ===========================
                                RELATIVE NAME
                            ============================ */}

                            {serviceRequestConfig.fields.relativeName.visible && (
                                <FTPInput
                                    className="col-span-1 min-w-0"
                                    name="profile.relation_name_eng"
                                    label={requiredLabel("relativeName", t("relative_name"))}
                                />
                            )}

                            {/* ===========================
                                CASTE
                            ============================ */}

                            {serviceRequestConfig.fields.caste.visible && (
                                <FTPAutoComplete
                                    className="col-span-1 min-w-0"
                                    name="caste"
                                    label={requiredLabel("caste", t("caste"))}
                                    data={casteOptions}
                                    lookupKey="label"
                                    placeholder={t("select_caste")}
                                />
                            )}

                            {/* ===========================
                                ADDRESS
                            ============================ */}

                            {serviceRequestConfig.fields.address.visible && (
                                <FTPInput
                                    className="col-span-1 min-w-0"
                                    name="profile.address"
                                    label={requiredLabel("address", t("address"))}
                                />
                            )}

                            {/* ===========================
                                DATE OF BIRTH
                            ============================ */}

                            {serviceRequestConfig.fields.dob.visible && (
                                <FTPInput
                                    className="col-span-1 min-w-0"
                                    type="date"
                                    name="dob"
                                    label={requiredLabel("dob", t("dob"))}
                                    inputProps={{
                                        max: new Date().toISOString().split("T")[0],
                                    }}
                                />
                            )}

                            {/* ===========================
                                AGE
                            ============================ */}

                            {serviceRequestConfig.fields.age.visible && (
                                <FTPInput
                                    className="col-span-1 min-w-0"
                                    name="profile.age"
                                    label={requiredLabel("age", t("age"))}
                                    numericOnly
                                    maxLength={3}
                                />
                            )}

                            {/* ===========================
                                GENDER
                            ============================ */}

                            {serviceRequestConfig.fields.gender.visible && (
                                <div className="col-span-1 min-w-0">
                                    <FTPRadioGroup
                                        name="profile.gender"
                                        label={requiredLabel("gender", t("gender"))}
                                        options={GENDERS_TRANSLATED}
                                    />
                                </div>
                            )}

                            {/* ===========================
                                BOOTH
                            ============================ */}

                            {serviceRequestConfig.fields.booth.visible && (
                                <div className="col-span-1 min-w-0">
                                    <RNBoothSelector
                                        label={requiredLabel("booth", t("booth"))}
                                        name="additional.booth"
                                        isMulti={false}
                                        selector="label"
                                    />
                                </div>
                            )}

                            {/* ===========================
                                VOTER PRESENCE
                            ============================ */}

                            {serviceRequestConfig.fields.voterPresence.visible && (
                                <div className="col-span-1 min-w-0">
                                    <FTPRadioGroup
                                        name="voter_presence"
                                        label={requiredLabel("voterPresence", t("voter_presence"))}
                                        options={IS_OUTSIDE_VOTER_TRANSLATED}
                                    />
                                </div>
                            )}

                            {/* ===========================
                                OTHER AC VOTER
                            ============================ */}

                            {serviceRequestConfig.fields.otherAcVoter.visible && (
                                <div className="col-span-1 min-w-0">
                                    <FTPRadioGroup
                                        name="is_outsider"
                                        label={requiredLabel("otherAcVoter", t("other_ac_voter"))}
                                        options={IS_OUTSIDE_VOTER_TRANSLATED}
                                    />
                                </div>
                            )}

                            {/* ===========================
                                OTHER AC VOTER DETAILS
                            ============================ */}

                            {serviceRequestConfig.fields.otherAcVoter.visible && isOtherACVoter && (
                                <div className="col-span-full min-w-0">
                                    <Fade direction="left">
                                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
                                            <FTPInput name="oac_name" label="Assembly Name" />

                                            <FTPInput name="oac_voter_name" label="Voter Name" />

                                            <FTPInput
                                                name="oac_voter_phonenumber"
                                                label="Phone Number"
                                                numericOnly
                                                maxLength={10}
                                            />
                                        </div>
                                    </Fade>
                                </div>
                            )}

                            <ServiceRequestCustomFields fields={subjectCustomFields} storagePath="subject" />
                        </div>
                    </div>

                    {/* =================================================
                        FAMILY MEMBERS
                    ================================================== */}

                    {serviceRequestConfig.fields.familyMembers.visible && (
                        <div className="flex h-[45vh] w-full flex-col gap-y-4 self-start overflow-y-auto rounded-xl border border-gray-400 bg-gray-200 p-4 sm:w-[40%]">
                            <FamilyMembers />
                            {(methods.formState.errors as any)?.additional?.family_members_list?.message && (
                                <p className="text-sm text-red-500">
                                    {(methods.formState.errors as any).additional.family_members_list.message}
                                </p>
                            )}
                        </div>
                    )}
                </div>

                {/* =====================================================
                    FORM BUTTONS
                ====================================================== */}

                <div className="flex flex-row items-center gap-4 self-end">
                    <FormButtons
                        onSave={() => {}}
                        onCancel={handleCancel}
                        saveText={t("save_and_continue")}
                    />
                </div>
            </form>
        </FormProvider>
    );
});

export default AddServiceRequestSubject;
